import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { activeGroup, filterNav, groupNav, type NavGroup, type NavItem } from "../nav";
import { NavIcon } from "./NavIcon";

/** Per-user remembered expanded/collapsed state of the sidebar groups. */
export const navStateKey = (userId: string) => "salon.nav.groups." + userId;
/** iPad / narrow screens: only the active group starts expanded. */
export const NARROW_NAV_QUERY = "(max-width: 1200px), (pointer: coarse)";
const isNarrow = () => { try { return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW_NAV_QUERY).matches; } catch { return false; } };
type GroupState = Partial<Record<NavGroup, boolean>>;
const load = (key: string): GroupState => { try { const v = JSON.parse(localStorage.getItem(key) ?? "{}"); return v && typeof v === "object" ? v as GroupState : {}; } catch { return {}; } };
const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

export function NavLinkItem({ item, onPick, badge }: { item: NavItem; onPick: () => void; badge?: number }) {
  const { t } = useI18n();
  return (
    <NavLink to={item.path} className={({ isActive }) => "nav-item" + (isActive ? " active" : "") + (item.locked ? " nav-locked" : "")} onClick={onPick} data-locked={item.locked ? "full" : undefined}>
      <NavIcon name={item.icon} />
      <span className="nav-label">{t(item.label)}</span>
      {item.locked && <span className="nav-plan-badge" title={t("tw.up.badgeTitle")}>{t("tw.full.badge")}</span>}
      {!!badge && <span className="nav-badge" aria-label={String(badge)}>{badge > 99 ? "99+" : badge}</span>}
    </NavLink>
  );
}

/**
 * Task L: owner / manager sidebar — header-less daily items, collapsible groups (the group of the current page
 * auto-expands; state remembered per user), Settings pinned at the bottom, and a quick search (Ctrl/Cmd+K).
 */
export function GroupedNav({ items, userId, badgeOf, onPick, onRequestOpen, footer }: {
  items: NavItem[]; userId: string; badgeOf: (i: NavItem) => number; onPick: () => void; onRequestOpen?: () => void; footer?: ReactNode;
}) {
  const { t } = useI18n();
  const loc = useLocation();
  const navigate = useNavigate();
  const key = navStateKey(userId);
  const [narrow] = useState(isNarrow);
  const [stored, setStored] = useState<GroupState>(() => load(key));
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const active = activeGroup(loc.pathname, items);
  const save = (next: GroupState) => { setStored(next); try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ } };
  const expanded = (g: NavGroup) => g === "daily" || g === "pinned" || (stored[g] ?? (narrow ? g === active : true));
  // the group containing the current page opens (and stays remembered as open)
  useEffect(() => { if (active && !expanded(active)) save({ ...stored, [active]: true }); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setStored(load(key)); }, [key]);
  useEffect(() => {
    const h = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault(); onRequestOpen?.();
        input.current?.focus(); input.current?.select();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onRequestOpen]);

  const searching = query.trim().length > 0;
  const shown = searching ? filterNav(items, query, (i) => t(i.label)) : items;
  const sections = groupNav(shown);
  const body = sections.filter((s) => s.id !== "pinned");
  const pinned = searching ? [] : items.filter((i) => i.group === "pinned");
  const pick = () => { setQuery(""); onPick(); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && shown[0]) { e.preventDefault(); navigate(shown[0].path); pick(); input.current?.blur(); }
    else if (e.key === "Escape") { setQuery(""); input.current?.blur(); }
  };

  return (
    <>
      <div className="nav-search" role="search">
        <NavIcon name="search" size={15} className="nav-search-icon" />
        <input ref={input} type="search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKey}
          placeholder={t("nav.search")} aria-label={t("nav.search")} data-testid="nav-search" autoComplete="off" spellCheck={false} />
        {!searching && <kbd className="nav-kbd" aria-hidden>{isMac() ? "⌘K" : "Ctrl K"}</kbd>}
      </div>
      <nav className="nav nav-grouped" data-testid="owner-nav">
        {body.map((s) => {
          const open = searching || expanded(s.id);
          const id = "nav-sec-" + s.id;
          return (
            <div key={s.id} className={"nav-section" + (s.id === active ? " has-active" : "")} data-group={s.id}>
              {s.label && (
                <button type="button" className={"nav-group-head" + (open ? " open" : "")} aria-expanded={open} aria-controls={id}
                  onClick={() => !searching && save({ ...stored, [s.id]: !open })}>
                  <span>{t(s.label)}</span>
                  <NavIcon name="chevron" size={14} className="nav-chevron" />
                </button>
              )}
              {open && (
                <div id={id} className="nav-section-items">
                  {s.items.map((i) => <NavLinkItem key={i.path} item={i} onPick={pick} badge={badgeOf(i)} />)}
                </div>
              )}
            </div>
          );
        })}
        {searching && sections.length === 0 && <p className="nav-empty muted small">{t("nav.search.none")}</p>}
        {searching && sections.some((s) => s.id === "pinned") && sections.find((s) => s.id === "pinned")!.items.map((i) => <NavLinkItem key={i.path} item={i} onPick={pick} />)}
      </nav>
      <div className="nav-bottom">
        {pinned.map((i) => <NavLinkItem key={i.path} item={i} onPick={pick} badge={badgeOf(i)} />)}
        {footer}
      </div>
    </>
  );
}
